import { useSearchParams, Link } from "react-router-dom";
import ServiceCard from "../components/ServiceCard";
import SearchInput from "../components/SearchInput";
import { useGetServicesQuery } from "../store/api/servicesApi";

export default function SearchResults() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  const { data } = useGetServicesQuery();
  const allServices = data?.data || [];

  const results = query.trim()
    ? allServices.filter(
        (s) =>
          s.title.toLowerCase().includes(query.toLowerCase()) ||
          (s.category || "").toLowerCase().includes(query.toLowerCase()) ||
          (s.description || "").toLowerCase().includes(query.toLowerCase())
      )
    : allServices;

  return (
    <div>
      {/* Hero banner */}
      <div className="relative bg-[#1a7b7b]">
        {/* Decorative blobs — clipped independently so dropdown isn't affected */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute -top-10 -right-10 w-52 md:w-72 opacity-80"
            style={{
              background: "linear-gradient(135deg, #a8edea 0%, #fed6e3 40%, #c3f0e8 70%, #b8d4f8 100%)",
              borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%",
              height: "220px",
              filter: "blur(2px)",
            }}
          />
        </div>

        <div className="container mx-auto px-4 py-14 md:py-20 flex flex-col items-center gap-4 relative z-10">
          {/* Breadcrumb */}
          <div className="flex flex-wrap items-center justify-center gap-1 text-white/70 font-manrope-regular text-sm">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link to="/services" className="hover:text-white transition-colors">
              All services
            </Link>
            <span>/</span>
            <span className="text-white">Search results</span>
          </div>

          <h1 className="font-manrope-bold text-[28px] md:text-[42px] text-white text-center">
            {query ? (
              <>
                Results for{" "}
                <span className="text-white/80">"{query}"</span>
              </>
            ) : (
              "All services"
            )}
          </h1>

          {/* Search — pre-filled with current query */}
          <div className="flex justify-center w-full max-w-lg">
            <SearchInput />
          </div>
        </div>
      </div>

      {/* Results area */}
      <div className="bg-bg py-12 md:py-16">
        <div className="container mx-auto px-4 flex flex-col gap-6">

          {/* Result meta */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="font-manrope-regular text-sm text-secondary">
              {results.length > 0 ? (
                <>
                  Showing{" "}
                  <span className="font-manrope-semibold text-black">
                    {results.length}
                  </span>{" "}
                  service{results.length !== 1 ? "s" : ""}
                  {query && (
                    <>
                      {" "}for{" "}
                      <span className="font-manrope-semibold text-black">
                        "{query}"
                      </span>
                    </>
                  )}
                </>
              ) : (
                "No services found"
              )}
            </p>

            {query && (
              <Link
                to="/services"
                className="font-manrope-medium text-sm text-primary hover:opacity-80 transition-opacity"
              >
                View all services →
              </Link>
            )}
          </div>

          {/* Grid */}
          {results.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {results.map((service) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 py-24">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-14 h-14 text-gray-300"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                />
              </svg>
              <p className="font-manrope-bold text-lg text-secondary">
                No results for "{query}"
              </p>
              <p className="font-manrope-regular text-sm text-gray-400">
                Try a different keyword or browse all services
              </p>
              <Link
                to="/services"
                className="mt-2 bg-primary text-white font-manrope-semibold text-sm px-6 py-2.5 rounded-xl hover:opacity-90 transition-opacity"
              >
                Browse all services
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
